import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { Redirect, useHistory, useParams } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/DropdownStyles.css";
import myToast from "../Utils/MyToast";
import Iframe from "react-iframe";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectMultipleStudent from "../dialog/MdlSelectMultipleStudent";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade8} 100%)`;
const ACCENT = COLORS.primary_color_shade5;
const ACCENT_DARK = COLORS.primary_color_shade7;

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const sectionLabelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 10,
  display: "flex",
  alignItems: "center",
  gap: 7,
};

function getCurrentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildSessionMonths() {
  try {
    const mSession = SessionManager.getCurrentSession();
    const start = new Date(mSession[0].start_date);
    const end = new Date(mSession[0].end_date);
    const months = [];
    while (start <= end) {
      months.push(
        `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`
      );
      start.setMonth(start.getMonth() + 1);
    }
    return months;
  } catch (_) {
    return [getCurrentYearMonth()];
  }
}

function formatMonthLabel(value) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(value || "");
  const names = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const idx = parseInt(m[2], 10) - 1;
  return names[idx] ? `${names[idx]} ${m[1]}` : String(value);
}

const DemandSlip = () => {
  const token = localStorage.getItem("token");
  const [loggedIn] = useState(!!token);
  const history = useHistory();
  const { document_type: documentType } = useParams();

  const [loading, setLoading] = useState(false);
  const [showPreviewLoader, setShowPreviewLoader] = useState(false);
  const [isOpenSelectStudent, setIsOpenSelectStudent] = useState(false);
  const [printableSamples, setPrintableSamples] = useState([]);
  const [selectedSamplePosition, setSelectedSamplePosition] = useState(0);
  const [selectedColorPosition, setSelectedColorPosition] = useState(0);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());
  const [fileUrl, setFileUrl] = useState("");

  const monthOptions = useMemo(() => buildSessionMonths(), []);

  const samplesRef = useRef(printableSamples);
  samplesRef.current = printableSamples;

  const colorOptions =
    printableSamples[selectedSamplePosition]?.color_options || [];

  const buildFileUrl = useCallback(
    (opts) => {
      const {
        samples = samplesRef.current,
        samplePosition = selectedSamplePosition,
        colorPosition = selectedColorPosition,
        students = selectedStudents,
        month = selectedMonth,
      } = opts || {};

      const sample = samples[samplePosition];
      if (!sample || students.length === 0) return "";

      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const studentIds = students.map((s) => s.student_enrollment_no);
      const sessionId = students[0]?.session_id || "";
      const classId = students[0]?.class_id || "";
      const sectionId = students[0]?.section_id || "";
      const colorOptionId =
        sample.color_options?.[colorPosition]?.color_option_id || "";

      return (
        process.env.REACT_APP_SERVER_NAME +
        sample.file_directory +
        sample.file_name +
        "?&student_ids=" + JSON.stringify(studentIds) +
        "&class_id=" + classId +
        "&section_id=" + sectionId +
        "&session_id=" + sessionId +
        "&selected_month=" + month +
        "&sample_id=" + sample.sample_id +
        "&selection_type=student" +
        "&system_code=" + process.env.REACT_APP_SYSTEM_CODE +
        "&color_option_id=" + colorOptionId +
        "&school_code=" + SCHOOL_CODE
      );
    },
    [selectedSamplePosition, selectedColorPosition, selectedStudents, selectedMonth]
  );

  const refreshPreview = useCallback(
    (opts) => {
      const url = buildFileUrl(opts);
      if (!url) return;
      console.log("FILE_URL", url);
      setShowPreviewLoader(true);
      setFileUrl(url);
    },
    [buildFileUrl]
  );

  const getPrintableSamples = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API_BASE()}getPrintableSample.php`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          document_type: documentType,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setPrintableSamples(response.data || []);
          setSelectedSamplePosition(0);
          setSelectedColorPosition(0);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log("getPrintableSamples error:", error);
      });
  }, [documentType]);

  useEffect(() => {
    getPrintableSamples();
  }, [getPrintableSamples]);

  const handleSelectedStudent = (mSelectedStudent) => {
    const students = mSelectedStudent || [];
    setIsOpenSelectStudent(false);
    setSelectedStudents(students);
    refreshPreview({ students });
  };

  const changeSample = (position) => {
    setSelectedSamplePosition(position);
    setSelectedColorPosition(0);
    refreshPreview({ samplePosition: position, colorPosition: 0 });
  };

  const changeColor = (position) => {
    setSelectedColorPosition(position);
    refreshPreview({ colorPosition: position });
  };

  const changeMonth = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    refreshPreview({ month });
  };

  const documentCaption =
    printableSamples[0]?.document_caption || "Demand Slip";

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div className="content" style={{ background: COLORS.white }}>
      {/* ── Breadcrumb header ── */}
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#" style={{ color: COLORS.white }}>
                Printable - {documentCaption}
              </a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      {/* ── Body card ── */}
      <div
        className="animated fadeInUpn bg_page_white_rounded box_shadow"
        style={{ "--mMargin": "30px", padding: 0, overflow: "hidden" }}
      >
        {/* Gradient title bar */}
        <div
          style={{
            width: "100%",
            background: HEADER_GRADIENT,
            padding: "16px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <h3
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "#fff",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <i className="fa fa-file-text" aria-hidden style={{ color: "#fff" }} />
            {documentCaption}
          </h3>

          {selectedStudents.length > 0 && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: COLORS.white,
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: 999,
                padding: "6px 14px",
              }}
            >
              <i className="fa fa-users" aria-hidden style={{ marginRight: 6 }} />
              {selectedStudents.length} student
              {selectedStudents.length === 1 ? "" : "s"} selected
            </span>
          )}
        </div>

        <div style={{ padding: "16px 28px 24px" }}>
          {/* ── Students + Month row ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <div>
              <p style={sectionLabelStyle}>
                <i className="fa fa-users" style={{ color: ACCENT }} aria-hidden />
                Students
              </p>
              <button
                type="button"
                onClick={() => setIsOpenSelectStudent(true)}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 10,
                  padding: "11px 16px",
                  background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
                  color: COLORS.white,
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <i className="fa fa-hand-pointer-o" aria-hidden />
                Select Students Manually
              </button>
            </div>

            <div>
              <p style={sectionLabelStyle}>
                <i className="fa fa-calendar" style={{ color: ACCENT }} aria-hidden />
                Month
              </p>
              <select
                value={selectedMonth}
                onChange={changeMonth}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1.5px solid ${COLORS.primary_color_shade3}`,
                  background: COLORS.white,
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                {monthOptions.map((item) => (
                  <option key={item} value={item}>
                    {formatMonthLabel(item)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Template picker ── */}
          {printableSamples.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <p style={sectionLabelStyle}>
                <i className="fa fa-clone" style={{ color: ACCENT }} aria-hidden />
                Select Template
              </p>
              <div className="d-flex flex-wrap" style={{ gap: 14 }}>
                {printableSamples.map((item, index) => {
                  const selected = selectedSamplePosition === index;
                  return (
                    <div
                      key={item.sample_id || index}
                      onClick={() => changeSample(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          changeSample(index);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        width: 168,
                        borderRadius: 12,
                        border: selected
                          ? `2px solid ${ACCENT}`
                          : "2px solid #e2e8f0",
                        background: selected
                          ? COLORS.primary_color_shade0
                          : COLORS.white,
                        boxShadow: selected
                          ? `0 4px 14px ${COLORS.primary_color_shade2}55`
                          : "0 1px 4px rgba(15,23,42,0.06)",
                        transition: "all 0.18s ease",
                        overflow: "hidden",
                        position: "relative",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = COLORS.primary_color_shade3;
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(15,23,42,0.1)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.boxShadow =
                            "0 1px 4px rgba(15,23,42,0.06)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }
                      }}
                    >
                      {selected && (
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: ACCENT,
                            color: COLORS.white,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            boxShadow: "0 2px 6px rgba(3,169,244,0.4)",
                          }}
                        >
                          <i className="fa fa-check" aria-hidden />
                        </div>
                      )}

                      {/* Name first (reversed from image-first layout) */}
                      <div
                        style={{
                          padding: "10px 10px 8px",
                          textAlign: "center",
                          fontSize: 13,
                          fontWeight: selected ? 800 : 600,
                          color: selected ? ACCENT_DARK : "#334155",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderBottom: selected
                            ? `1px solid ${COLORS.primary_color_shade3}`
                            : "1px solid #eef2f7",
                          background: selected
                            ? COLORS.primary_color_shade0
                            : "#f8fafc",
                        }}
                        title={item.sample_name}
                      >
                        {item.sample_name}
                      </div>

                      <div
                        style={{
                          height: 110,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: COLORS.white,
                          overflow: "hidden",
                          padding: 8,
                        }}
                      >
                        {item.sample_img ? (
                          <img
                            src={item.sample_img}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback = e.currentTarget.nextSibling;
                              if (fallback) fallback.style.display = "flex";
                            }}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              borderRadius: 4,
                            }}
                          />
                        ) : null}
                        <div
                          style={{
                            display: item.sample_img ? "none" : "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 6,
                            color: "#94a3b8",
                          }}
                        >
                          <i className="fa fa-file-o" style={{ fontSize: 28 }} aria-hidden />
                          <span style={{ fontSize: 11, fontWeight: 600 }}>Preview</span>
                        </div>
                      </div>

                      {selected && (
                        <div style={{ height: 3, background: ACCENT }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Color options ── */}
          {colorOptions.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <p style={sectionLabelStyle}>
                <i className="fa fa-paint-brush" style={{ color: ACCENT }} aria-hidden />
                Color Option
              </p>
              <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                {colorOptions.map((item, index) => {
                  const [color1, color2] = String(item.sample_color_code || "")
                    .split("_");
                  const selected = selectedColorPosition === index;
                  return (
                    <div
                      key={item.color_option_id || index}
                      onClick={() => changeColor(index)}
                      style={{
                        cursor: "pointer",
                        width: 86,
                        borderRadius: 10,
                        border: selected
                          ? `2px solid ${ACCENT}`
                          : "2px solid #e2e8f0",
                        background: selected
                          ? COLORS.primary_color_shade0
                          : "#f8fafc",
                        overflow: "hidden",
                        transition: "all 0.18s ease",
                      }}
                    >
                      <div style={{ display: "flex", height: 38 }}>
                        <div style={{ background: color1, width: "50%" }} />
                        <div style={{ background: color2, width: "50%" }} />
                      </div>
                      <div
                        style={{
                          padding: "4px 6px",
                          textAlign: "center",
                          fontSize: 11,
                          fontWeight: selected ? 700 : 500,
                          color: selected ? ACCENT_DARK : "#475569",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.sample_color_name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Divider ── */}
          <div style={{ height: 1, background: "#e2e8f0", marginBottom: 14 }} />

          {/* ── Preview ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <i className="fa fa-eye" style={{ color: ACCENT, fontSize: 16 }} aria-hidden />
            <span style={{ fontWeight: 600, fontSize: 15, color: "#334155" }}>
              Preview
            </span>
          </div>

          {selectedStudents.length === 0 ? (
            <div
              style={{
                padding: "34px 16px",
                borderRadius: 12,
                background: "#f8fafc",
                border: "1px dashed #cbd5e1",
                textAlign: "center",
                color: "#64748b",
                fontSize: 13,
              }}
            >
              <i
                className="fa fa-users"
                aria-hidden
                style={{ fontSize: 26, color: "#94a3b8", display: "block", marginBottom: 8 }}
              />
              Select students to generate the demand slip preview.
            </div>
          ) : (
            <div
              style={{
                borderRadius: 10,
                overflow: "hidden",
                border: `1px solid ${COLORS.primary_color_shade2}`,
                position: "relative",
                minHeight: 320,
              }}
            >
              {showPreviewLoader && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.88)",
                  }}
                  aria-busy="true"
                >
                  <MyLoader />
                </div>
              )}
              <Iframe
                url={fileUrl}
                width="100%"
                height="800px"
                id="iFrameDemandSlip"
                frameBorder="0"
                display="initial"
                position="relative"
                onLoad={() => setShowPreviewLoader(false)}
              />
            </div>
          )}
        </div>
      </div>

      {isOpenSelectStudent && (
        <MdlSelectMultipleStudent
          actionClose={() => setIsOpenSelectStudent(false)}
          actionHandleSelected={handleSelectedStudent}
        />
      )}
    </div>
  );
};

export default DemandSlip;
